<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import UIKit from '@pocketprep/ui-kit'
import { userModule } from '@/store/user/module'
import { assignmentModule } from '@/store/assignment/module'
import { quizModule } from '@/store/quiz/module'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import { bundleModule } from '@/store/bundle/module'
import { analyticsModule } from '@/store/analytics/module'
import AssignmentInfo from '@/components/Assignments/AssignmentInfo.vue'
import AssignmentError from '@/components/Assignments/AssignmentError.vue'
import { mockExamModule } from '@/store/mockExam/module'
import UnfinishedQuizModal from '@/components/Study/UnfinishedQuizModal.vue'
import type { Study, Teach } from '@pocketprep/types'

const router = useRouter()
const route = useRoute()

const {
    Button: PocketButton,
    Icon,
    Errors,
} = UIKit

const isLoading = ref(true)
const assignmentId = ref('')
const errorMessage = ref('')
const errorDescription = ref('')
const errorCode = ref('')
const showUnfinishedQuizModal = ref(false)
const discardUnfinishedQuizExam = ref(false)
const clientVersion = ref(import.meta.env.VUE_APP_VERSION)

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const assignment = computed(() => {
    return assignmentModule.getters.getAssignment()
})

const assignmentOrgId = computed(() => {
    const assignmentOrg = assignment.value?.organization as Teach.Class.OrganizationJSON
    return assignmentOrg?.objectId
})

const completedAssignment = computed(() => {
    return assignmentModule.getters.getCompletedAssignment()
})

const userExamMetadata = computed(() => {
    return userExamMetadataModule.getters.getCurrentUserExamMetadata()
})

const currentExam = computed(() => {
    return examMetadataModule.getters.getCurrentExamMetadata()
})

const currentExamMajorVersion = computed(() => {
    return userExamMetadata.value?.examVersion?.split('.')[0] || '1'
})

const assignmentExamMetadata = computed(() => {
    return assignment.value?.examMetadata as Study.Class.ExamMetadataJSON
})

const assignmentExamMajorVersion = computed(() => {
    return assignmentExamMetadata.value?.version.split('.')[0] || '1'
})

const isStudyingWrongMajorExamVersion = computed(() => {
    return currentExam.value?.examGuid === assignmentExamMetadata.value?.examGuid &&
        assignmentExamMajorVersion.value !== currentExamMajorVersion.value
})

const wrongExamVersionError = computed(() => {
    return `This assignment was created for a different exam version: 
        ${assignmentExamMetadata.value?.releaseInfo.name ? 
            assignmentExamMetadata.value?.releaseInfo.name : assignmentExamMetadata.value?.nativeAppName},
        Version ${assignmentExamMetadata.value?.version}.
        You need to switch exam versions to take it.`
})

const examMetadataById = computed(() => {
    return examMetadataModule.getters.getExamMetadataById()
})

const currentBundle = computed(() => {
    return bundleModule.getters.getBundles()
        .find(b => b.exams.find(e => {
            const bundleExam = examMetadataById.value[e.objectId]
            return currentExam.value && bundleExam?.examGuid === currentExam.value.examGuid
        }))
})

const activeQuizOrAssignment = computed(() => {
    return quizModule.getters.getActiveQuiz()
})

const activeMockExam = computed(() => {
    return mockExamModule.getters.getActiveMockExamQuiz()
})

const quizOrExamToResume = computed(() => {
    return activeMockExam.value || activeQuizOrAssignment.value
})

const unfinishedQuizMode = computed(() => {
    return quizOrExamToResume.value?.mode
})

onMounted(async () => {
    isLoading.value = true

    await Promise.all([
        examMetadataModule.actions.fetchExamMetadata(),
        userExamMetadataModule.actions.fetchUserExamMetadata(),
        bundleModule.actions.fetchBundles(),
        mockExamModule.actions.fetchMockExams(),
    ])

    if (typeof route.query.assignment !== 'string' && typeof route.query.id !== 'string') {
        setUnableToLoadError()
    } else {
        assignmentId.value = route.query.assignment as string || route.query.id as string

        try {
            await assignmentModule.actions.fetchAssignment(assignmentId.value)

            if (completedAssignment.value) {
                await router.push({
                    name: 'quiz-result',
                    params: { quizId: completedAssignment.value.objectId },
                })
                isLoading.value = false
            }
        } catch (e: { errorDescription?: string; errorMessage?: string } | unknown) {
            if (e && typeof e === 'object' && 'errorMessage' in e) {
                errorMessage.value = e.errorMessage as string || ''
                errorDescription.value = 'errorDescription' in e && e.errorDescription as string || ''
                errorCode.value = 'errorCode' in e && e.errorCode as string || ''
            } else {
                setUnableToLoadError()
            }
        }
    }

    if (route.query.id as string || route.query.legacyLinkFollowed === 'true') {
        analyticsModule.actions.amplitudeTrack('Assignment Link Followed', {
            client: 'Web Study',
            clientAppId: 'study.pocketprep.com',
            clientVersion: clientVersion.value,
            examGuid: currentExam.value?.examGuid || '',
            bundleId: currentBundle.value?.objectId || '',
            organizationId: assignmentOrgId.value || '',
        })
    }

    isLoading.value = false
})

const setUnableToLoadError = () => {
    errorMessage.value = 'Unable to load'
    errorDescription.value = 'There was a problem loading your assignment. Please try again.'
    errorCode.value = 'unableToLoad'
    return
}

const backToStudy = async () => {
    await router.push({
        name: 'study',
    })
}

const goToSettings = async () => {
    await router.push({
        path: 'settings/exams',
        query: {
            ...route.query,
            examId: currentExam.value?.objectId,
        },
    })
}

const resumeMode = async () => {
    if (unfinishedQuizMode.value === 'mockExam') {
        await router.push({ name: 'mock-exam' })
    } else {
        await router.push({ name: 'quiz' })
    }
}

const startNewAssignment = () => {
    discardUnfinishedQuizExam.value = true
    showUnfinishedQuizModal.value = false
    goToAssignment()
}

const assignmentStartedAmplitudeEvent = () => {
    analyticsModule.actions.amplitudeTrack('Assignment Started', {
        client: 'Web Study',
        clientAppId: 'study.pocketprep.com',
        clientVersion: clientVersion.value,
        assignmentId: assignmentId.value,
        examGuid: currentExam.value?.examGuid || '',
        bundleId: currentBundle.value?.objectId || '',
        organizationId: assignmentOrgId.value || '',
    })
}

const goToAssignment = async () => {
    if (quizOrExamToResume.value && !discardUnfinishedQuizExam.value) {
        showUnfinishedQuizModal.value = true
        return
    }

    try {
        const completedQuiz = await quizModule.actions.createActiveQuiz({
            prebuiltQuizId: assignmentId.value,
            mode: 'assignment',
        })
        if (completedQuiz) {
            await router.push({
                name: 'quiz-result',
                params: { quizId: completedQuiz.objectId },
            })
            return
        }
    } catch (e: { errorDescription?: string; errorMessage?: string } | unknown) {
        if (e && typeof e === 'object' && 'errorMessage' in e) {
            if (e && typeof e === 'object' && 'errorMessage' in e) {
                errorMessage.value = e.errorMessage as string || ''
                errorDescription.value = 'errorDescription' in e && e.errorDescription as string || ''
                errorCode.value = 'errorCode' in e && e.errorCode as string || ''
            }
            return
        }
        throw e
    }

    assignmentStartedAmplitudeEvent()

    await router.push({
        path: '/quiz',
    })
}
</script>

<template>
    <div 
        class="assignment-intro__container"
        :class="{ 'assignment-intro__container--error': errorMessage || errorDescription || errorCode }"
    >
        <UnfinishedQuizModal
            v-if="showUnfinishedQuizModal"
            :unfinished-mode="unfinishedQuizMode"
            :clicked-mode="'assignment'"
            @close="showUnfinishedQuizModal = false"
            @startNew="startNewAssignment"
            @resume="resumeMode"
        />
        <div 
            id="assignment-intro"
            v-dark
            class="assignment-intro"
        >
            <Icon
                v-dark
                type="close"
                class="assignment-intro__close-icon"
                title="Close"
                tabindex="0"
                @click="backToStudy"
                @keydown.enter="backToStudy"
                @mousedown.prevent
            />
            <div 
                v-dark 
                class="assignment-intro__header"
                :class="{ 
                    'assignment-intro__header--wrong-exam-version-error': isStudyingWrongMajorExamVersion
                }"
            >
                <div
                    v-if="!errorMessage || !errorDescription || !errorCode"
                    v-dark
                    tabindex="-1"
                    class="assignment-intro__title"
                    role="header"
                >
                    <Icon
                        v-dark
                        type="assignment"
                        class="assignment-intro__title-icon"
                    />
                    Assignment
                </div>
            </div>
            <Icon
                v-if="isLoading"
                class="assignment-intro__loading"
                type="loading2"
                :is-dark-mode="isDarkMode"
            />
            <div 
                v-else-if="!isLoading && assignment && (!errorMessage || !errorDescription || !errorCode)" 
                class="assignment-intro__content"
            >
                <Errors
                    v-if="!isLoading && isStudyingWrongMajorExamVersion"
                    class="assignment-intro__wrong-exam-version-error"
                    :is-dark-mode="isDarkMode"
                    :errors="[ wrongExamVersionError ]"
                />
                <AssignmentInfo
                    v-if="!isLoading && assignment"
                    class="assignment-intro__info"
                    :assignment="assignment"
                />
                <div class="assignment-intro__reminders">
                    <div v-dark class="assignment-intro__reminders-label">
                        Reminders
                    </div>
                    <ul class="assignment-intro__reminders-list">
                        <li v-dark class="assignment-intro__reminders-list-item">
                            You can submit each assignment only once.
                        </li>
                        <li
                            v-if="assignment.dueDate"
                            v-dark 
                            class="assignment-intro__reminders-list-item"
                        >
                            After the due date, the assignment will no longer be available.
                        </li>
                        <li v-dark class="assignment-intro__reminders-list-item">
                            Once you have completed this assignment, 
                            it can be found in the Study Activity section of the Stats tab.
                        </li>
                    </ul>
                </div>
            </div>
            <AssignmentError
                v-else
                class="assignment-intro__error-message"
                :errorMessage="errorMessage"
                :errorDescription="errorDescription"
                :errorCode="errorCode"
                @close="backToStudy"
                @reviewExamVersions="goToSettings"
            />
            <div
                v-if="!isLoading"
                v-dark 
                class="assignment-intro__footer"
                :class="{ 'assignment-intro__footer--error': errorMessage || errorDescription || errorCode }"
            >
                <PocketButton
                    type="secondary"
                    :is-dark-mode="isDarkMode"
                    @click="backToStudy"
                >
                    Cancel
                </PocketButton>
                <PocketButton
                    v-if="!isLoading && isStudyingWrongMajorExamVersion"
                    class="assignment-intro__start-assignment-btn"
                    :is-dark-mode="isDarkMode"
                    @click="goToSettings"
                >
                    Review Exam Versions
                </PocketButton>
                <PocketButton
                    v-else
                    class="assignment-intro__start-assignment-btn"
                    :is-dark-mode="isDarkMode"
                    @click="goToAssignment"
                >
                    Start Assignment
                </PocketButton>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.assignment-intro {
    position: relative;
    background-color: $floral-white;
    display: flex;
    flex-direction: column;
    align-items: center;
    min-height: 100%;

    &--dark {
        background-color: $charcoal;
    }

    &__container {
        height: calc(100% - 87px);
        overflow: auto; // overflow:auto and position:relative need to be on different elements

        // Including breakpoint for mobile footer height calculation
        @include breakpoint(black-bear) {
            height: calc(100% - 68px);
        }

        &--error {
            height: 100%;
        }
    }

    &__content {
        z-index: 1;

        @include breakpoint(black-bear) {
            padding-left: 12px;
        }
    }

    &__close-icon {
        position: absolute;
        color: $brand-blue;
        width: 29px;
        height: 29px;
        top: 22px;
        right: 24px;
        cursor: pointer;

        &--dark {
            color: $banana-bread;
        }
    }

    &__header {
        margin-top: 90px;
        margin-bottom: 40px;
        width: 460px;
        padding-left: 16px;

        @include breakpoint(black-bear) {
            margin-top: 95px;
            margin-bottom: 16px;
            width: 290px;
            padding-left: 40px;
        }

        &--wrong-exam-version-error {
            @include breakpoint(black-bear) {
                margin-bottom: 26px;
            }
        }
    }

    &__title {
        outline: none;
        position: relative;
        display: flex;
        font-weight: 600;
        font-size: 26px;
        line-height: 34px;
        left: -20px;

        &--dark {
            color: $fog;
        }
    }

    &__title-icon {
        position: absolute;
        width: 24px;
        height: 24px;
        top: 2px;
        left: -35px;

        &--dark {
            color: $fog;
        }

        @include breakpoint(black-bear) {
            top: 1px;
            left: -35px;
        }
    }

    &__loading {
        margin-top: 200px;

        @include breakpoint(black-bear) {
            margin-top: 150px
        }
    }

    &__go-to-settings-btn {
        margin: 24px auto 0;
    }

    &__wrong-exam-version-error {
        width: 465px;
        margin-bottom: 34px;

        @include breakpoint(black-bear) {
            width: 304px;
            margin-bottom: 24px;
        }
    }

    &__reminders {
        display: flex;
        width: 482px;
        padding: 34px 20px 13px 15px;
        flex-direction: column;
        align-items: flex-start;

        @include breakpoint(black-bear) {
            width: 334px;
            padding: 20px 20px 13px 15px;
        }
    }

    &__reminders-label {
        color: $brand-black;
        font-size: 15px;
        font-weight: 600;
        line-height: 20px;
        margin-bottom: 14px;

        &--dark {
            color: $white;
        }
    }

    &__reminders-list {
        margin-top: 0;
        margin-left: -12px;
    }

    &__reminders-list-item {
        color: $brand-black;
        font-size: 15px;
        font-weight: 500;
        line-height: 22px;
        margin-bottom: 4px;

        &--dark {
            color: $fog;
        }
    }

    &__footer {
        position: fixed;
        bottom: 0;
        width: 100%;
        height: 87px;
        background-color: $white;
        display: flex;
        justify-content: center;
        align-items: center;
        border-top: 1px solid $fog;
        z-index: 2;

        &--dark {
            background-color: $mariner;
            border-color: $brand-black;
        }

        @include breakpoint(black-bear) {
            height: 68px;
        }

        &--error {
            display: none;
        }
    }

    &__start-btn {
        margin-left: 8px;
    }
}
</style>
